import { undefinedToNull } from '@core/prelude';

export type Collection<T> = { [id: string]: T };

export function collectionEmpty<T>(): Collection<T> {
  return {};
}

export function collectionSingleton<T>(id: string, x: T): Collection<T> {
  const collection: Collection<T> = {};
  collection[id] = x;
  return collection;
}

export function collectionFromArray<T extends { [s: string]: any }>(
  array: T[]
): Collection<T> {
  const collection: Collection<T> = {};

  array.forEach((element) => {
    const map: Map<string, any> = new Map(Object.entries(element));
    collection[map.get('id')] = element;
  });

  return collection;
}

export function collectionLength<T>(collection: Collection<T>): number {
  let length: number = 0;
  for (let _ in collection) length++;
  return length;
}

export function collectionItem<T>(
  collection: Collection<T>,
  id: string
): T | null {
  return id in collection ? undefinedToNull(collection[id]) : null;
}

export function collectionMapToArrayWithId<T, U>(
  collection: Collection<T>,
  f: (id: string, x: T) => U
): U[] {
  let result: U[] = [];
  for (let id in collection) {
    result.push(f(id, collection[id]));
  }
  return result;
}

export function collectionKeys<T>(collection: Collection<T>): string[] {
  return collectionMapToArrayWithId(collection, (id, _) => id);
}

export function collectionItems<T>(collection: Collection<T>): T[] {
  return collectionMapToArrayWithId(collection, (_, x) => x);
}

export function collectionIter<T>(
  collection: Collection<T>,
  f: (id: string, item: T) => void
): void {
  for (let id in collection) {
    f(id, collection[id]);
  }
}

export function collectionMapWithId<T, U>(
  collection: Collection<T>,
  f: (id: string, x: T) => U
): Collection<U> {
  const c: Collection<U> = {};
  for (let id in collection) {
    c[id] = f(id, collection[id]);
  }
  return c;
}

export function collectionMap<T, U>(
  collection: Collection<T>,
  f: (x: T) => U
): Collection<U> {
  return collectionMapWithId(collection, (_, x) => f(x));
}

export function collectionFilter<T>(
  collection: Collection<T>,
  predicate: (x: T) => boolean
): Collection<T> {
  const filtered: Collection<T> = {};
  for (let id in collection) {
    if (predicate(collection[id])) {
      filtered[id] = collection[id];
    }
  }
  return filtered;
}
